import { useEffect, useState } from 'react';

import { CLICK_IDS } from '@sunflower-games/analytics-events';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { useEffectOnce, useLocalStorage } from 'react-use';

import Button from '../../../components/buttons/Button';
import { DialogContent } from '../../../components/Dialog/DialogContent';
import { DialogHeader } from '../../../components/Dialog/DialogHeader';
import ImageWithFallback from '../../../components/ImageWithFallback/ImageWithFallback';
import { useLanderBanner } from '../../../hooks/useLanderBanner';
import { useMarketingConsents } from '../../../hooks/useMarketingConsents';
import { analytics } from '../../../services/analytics';
import { useFlag } from '../../../services/launchDarkly';
import { useStores } from '../../../stores/useStores';
import SignUpConsents from '../SignUpConsents/SignUpConsents';
import { ThirdPartyAuth } from '../ThirdPartyAuth/ThirdPartyAuth';
import { useOpenAuthWizard } from '../useOpenAuthWizard';
import { useHandleSignUpWizard } from '../wizards/useHandleSIgnUpWizard';

import styles from './SignUpOptions.module.scss';

export const SignUpOptions = observer(() => {
  const { t } = useTranslation();
  const [_signUpMarketingConsentsLS, setSignUpMarketingConsentsLS, removeSignUpMarketingConsentsLS] = useLocalStorage<
    Record<string, boolean>
  >('signup-marketing-consents', {});

  const { authStore, portalStore, marketingConsentStore, landingStore } = useStores();
  const { signUpMarketingConsents, fetchingSignUpMarketingConsent } = marketingConsentStore;

  const [marketingConsents, setMarketingConsents] = useState<Record<string, boolean>>({});
  const [initializedMarketingConsents, setInitializedMarketingConsents] = useState(false);

  const { openAuthWizard } = useOpenAuthWizard();

  const landingKey = authStore.getLandingKey();
  const isDirectSignUpLanding = authStore.isDirectSignUpLanding();
  const { bannerUrl } = useLanderBanner('marketing');
  const blockSignupWithEmailFeatureFlag = useFlag('block_signup_with_email', false);
  const [bypassBlockSignupWithEmail, setBypassBlockSignupWithEmail] = useState(false);
  const [isAgeConfirmed, setIsAgeConfirmed] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [approveError, setApproveError] = useState('');

  const { handleSignUpWizard } = useHandleSignUpWizard();

  const {
    marketingConsents: marketingConsentsFromHook,
    onChangeMarketingConsent,
    setMarketingConsentsLS,
  } = useMarketingConsents({
    initialMarketingConsents: signUpMarketingConsents,
    fetchingMarketingConsents: fetchingSignUpMarketingConsent,
    localStorageKey: 'signup-marketing-consents',
  });

  useEffect(() => {
    const eventHandler = (event: MessageEvent) => {
      if (event.data === 'bypassBlockSignupWithEmail') {
        setBypassBlockSignupWithEmail(true);
      }
    };

    window.addEventListener('message', eventHandler);
    return () => {
      window.removeEventListener('message', eventHandler);
    };
  }, []);

  useEffectOnce(() => {
    if (isDirectSignUpLanding) {
      landingStore.sendLandingPageEvent('direct_su');
    }
  });

  useEffect(() => {
    if (!isDirectSignUpLanding) {
      // If not a direct sign-up landing, it's already set in previous step (signUpPrivacy)
      setInitializedMarketingConsents(true);
      return;
    }

    if (!fetchingSignUpMarketingConsent) {
      if (signUpMarketingConsents.length > 0) {
        const map = signUpMarketingConsents.reduce(
          (acc, consent) => {
            acc[consent.id] = false;
            return acc;
          },
          {} as Record<string, boolean>,
        );

        setMarketingConsents(map);
        setInitializedMarketingConsents(true);
      } else {
        setMarketingConsents({});
        setInitializedMarketingConsents(true);
      }
    }
  }, [signUpMarketingConsents, fetchingSignUpMarketingConsent, isDirectSignUpLanding]);

  const handleLogin = () => {
    void analytics.sendEvent('log_in', {
      step_name: 'log_in_start',
      event_action: 'click',
      click_id: CLICK_IDS.AUTH_FLOW.LOGIN_START,
      button_location: 'sign_up_menu',
      landingKey,
    });

    void analytics.sendEvent('log_in', {
      step_name: 'log_in_platform',
      event_action: 'impression',
      triggered_by: 'sign_up_platform',
      landingKey,
    });

    openAuthWizard('logInWizard', {
      triggeredBy: 'sign_up_platform',
      force: true,
      persistent: isDirectSignUpLanding,
      landingKey: landingKey ?? undefined,
    });
  };

  const blockSignupWithEmail = blockSignupWithEmailFeatureFlag && !bypassBlockSignupWithEmail;

  const isApprovalFieldsValid = Boolean(!isDirectSignUpLanding || (isAgeConfirmed && isTermsAccepted));

  const validateApprovalFields = () => {
    if (!isApprovalFieldsValid) {
      setApproveError(t('auth.approvalConditionsRequired'));
    }

    if (isApprovalFieldsValid && isDirectSignUpLanding) {
      setSignUpMarketingConsentsLS(marketingConsents);
    }

    return isApprovalFieldsValid;
  };

  const handleEmailSignUp = () => {
    const isValid = validateApprovalFields();

    if (!isValid) {
      return;
    }
    setMarketingConsentsLS(marketingConsentsFromHook);

    handleSignUpWizard({ provider: 'email' });
  };

  const onChangeMarketingConsentCheckBox = (checked: boolean, consentId: string) => {
    onChangeMarketingConsent(checked, consentId);
    setMarketingConsents((prev) => ({ ...prev, [consentId]: checked }));
  };

  const onClose = () => {
    portalStore.closePortal();
    removeSignUpMarketingConsentsLS();
  };

  if (isDirectSignUpLanding && !initializedMarketingConsents) {
    return null;
  }

  return (
    <div className={classNames(styles.signUpOptions)}>
      <DialogHeader title={t('auth.createAccount')} onClose={onClose} />

      <DialogContent className={classNames(styles.signUpOptionsContent)}>
        {bannerUrl && <ImageWithFallback alt="Landing" className={styles.landerBannerImg} src={bannerUrl} />}

        <ThirdPartyAuth action="Sign Up" onLoginAttempt={validateApprovalFields} />

        {!blockSignupWithEmail && (
          <Button btnType="main" className={styles.emailSignUpButton} onClick={handleEmailSignUp}>
            {t('auth.signUpWithEmail')}
          </Button>
        )}
        {isDirectSignUpLanding && (
          <SignUpConsents
            approveError={approveError}
            isAgeConfirmed={isAgeConfirmed}
            isTermsAccepted={isTermsAccepted}
            marketingConsents={marketingConsentsFromHook}
            setIsAgeConfirmed={setIsAgeConfirmed}
            setIsTermsAccepted={setIsTermsAccepted}
            signUpMarketingConsents={signUpMarketingConsents}
            onChangeMarketingConsent={onChangeMarketingConsentCheckBox}
          />
        )}
        <div
          className={classNames({
            [styles.loginSectionMt]: isDirectSignUpLanding,
            'animation-slide-down': true,
            'form-links': true,
          })}
        >
          <span>{t('auth.alreadyHaveAccountQuestion')}</span>
          <span className="text-link" onClick={handleLogin}>
            {t('auth.logIn')}
          </span>
        </div>
      </DialogContent>
    </div>
  );
});
